/**
 * Manages the AI Edits adapter lifecycle within React.
 * Creates a single adapter instance, handles cleanup on clip changes,
 * and provides an initialize function for prefetching data.
 */

import { useCallback, useEffect, useMemo } from 'react';
import type { Channel } from 'pusher-js';
import type { AgenticContext } from 'agentic_chat/types';
import appLogger from '@/services/logger';
import { AIEditsAdapter } from '@/adapters/aiEdits/AIEditsAdapter';
import { clearCache } from '@/adapters/aiEdits/cache';
import { aiEditsStore } from '@/stores/aiEditsStore';
import { showSuccessToast, showErrorToast } from '@/libs/toast/toast';

export interface UseAIEditsAdapterOptions {
  clipId?: string;
  orgChannel?: Channel | null;
}

export interface UseAIEditsAdapterReturn {
  /** The adapter instance, or null if orgChannel is not available */
  adapter: AIEditsAdapter | null;
  /** Prefetch data for the sidebar (recipes, status, history) */
  initialize: () => Promise<void>;
  /** Build context object for the current clip */
  buildContext: () => AgenticContext | null;
}

export function useAIEditsAdapter({ clipId, orgChannel }: UseAIEditsAdapterOptions): UseAIEditsAdapterReturn {
  const adapter = useMemo(() => {
    if (!orgChannel) {
      return null;
    }
    appLogger.info('useAIEditsAdapter: Creating adapter', {
      channelName: orgChannel?.name
    });
    return new AIEditsAdapter(orgChannel);
  }, [orgChannel]);

  const buildContext = useCallback((): AgenticContext | null => {
    if (!clipId) {
      return null;
    }

    return {
      type: 'AI_EDITS',
      metadata: { clipId },
      features: {
        recipes: true,
        helpActions: true,
        suggestions: true,
        streaming: true,
        messageOptions: true
      },
      display: {
        title: 'AI Edits',
        placeholder: 'What do you want to do?',
        emptyStateMessage: 'Start a conversation to get AI-powered suggestions.'
      },
      toast: {
        success: showSuccessToast,
        error: showErrorToast
      }
    };
  }, [clipId]);

  // Initialize function - handles module/CSS + data prefetch
  const initialize = useCallback(async (): Promise<void> => {
    if (!adapter || !clipId) {
      return;
    }

    const context = buildContext();
    if (!context) {
      return;
    }

    appLogger.info('useAIEditsAdapter: Initializing adapter', { clipId });

    try {
      // This prefetches BOTH module/CSS + data
      await adapter.initialize(context);
    } catch (error) {
      appLogger.error('useAIEditsAdapter: Error during initialization', error);
      // Silent fail - components will fetch fresh data if initialization fails
    }
  }, [adapter, clipId, buildContext]);

  useEffect(() => {
    if (!adapter || !clipId) {
      return;
    }

    initialize();

    return () => {
      appLogger.info('useAIEditsAdapter: Cleaning up on unmount');
      clearCache();
      aiEditsStore.reset();
    };
  }, [clipId, adapter, initialize]);

  return {
    adapter,
    initialize,
    buildContext
  };
}

export default useAIEditsAdapter;
